import { ImpactMethod } from "@/hooks/useImpactMethod";
import useSmartQueryClient from "@/hooks/useSmartQueryClient";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";

export interface EpdGetOrPost {
  impact_method: ImpactMethod;
  activity_name: string;
  geography: string;
  unit: number;
  sector: number;
  ipcc_global_warming_potential?: boolean;
  acidification: number;
  toxicity_freshwater: number;
  toxicity_marine: number;
  toxicity_terrestrial: number;
  eutrophication_freshwater: number;
  eutrophication_marine: number;
  energy_resources: number;
  climate_change: number;
  human_toxicity_carcinogenic: number;
  human_toxicity_non_carcinogenic: number;
  ionising_radiation: number;
  land_use: number;
  material_resources: number;
  ozone_depletion: number;
  particulate_matter: number;
  photochemical_oxidant_human_health: number;
  photochemical_oxidant_terrestrial: number;
  water_use: number;
}

export function useGetEpd({ id }: { id?: number }) {
  return useQuery({
    queryKey: ["epd", id],
    keepPreviousData: true,
    enabled: !!id,
    queryFn: () =>
      axios.get<EpdGetOrPost>("/impact_references/epds/" + id).then((result) => result.data),
  });
}

export function usePostEpd() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ post }: { post: EpdGetOrPost }) => axios.post("/impact_references/epds", post),
    onSuccess: () => {
      smartQueryClient.invalidateQueries(["datasets"]);
    },
  });
}

export function useUpdateEpd() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ id, post }: { id: number; post: EpdGetOrPost }) =>
      axios.post("/impact_references/epds/" + id, post),
    onSuccess: (response) => {
      smartQueryClient.invalidateQueries(["epd", response.data.id]);
      smartQueryClient.invalidateQueries(["datasets"]);
    },
  });
}

export function useDeleteEpd() {
  const smartQueryClient = useSmartQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id?: number }) => axios.delete("/impact_references/epds/" + id),
    onSuccess: (response) => {
      smartQueryClient.invalidateQueries(["datasets"]);
    },
  });
}
